import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Lock, ArrowRight, AlertCircle, CheckCircle } from 'lucide-react';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { useTranslation } from 'react-i18next';
import { MIN_PASSWORD_LENGTH } from '../lib/constants';
import { meetsPasswordComplexity } from '../lib/validation';
import { detailToMessage } from '../lib/apiError';
import { PasswordInput } from '../components/ui/PasswordInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('reset_password.error_min_length', { min: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (!meetsPasswordComplexity(password)) {
      setError(t('auth.password_complexity'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('reset_password.error_passwords_match'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(detailToMessage(err.response?.data?.detail) || t('auth.something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-8">
        <div className="w-full max-w-md text-center">
          <p className="text-gray-400 mb-4">{t('reset_password.invalid_link')}</p>
          <Link to="/forgot-password" className="text-amber-400 hover:text-amber-300">
            {t('reset_password.request_new_link')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex">
      <div className="flex-1 flex flex-col items-center px-8 py-12">
        <div className="w-full max-w-md my-auto">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <BusyBeeLogo className="h-10" />
          </Link>

          <h1 className="text-3xl font-bold text-white mb-2">{t('reset_password.title')}</h1>
          <p className="text-gray-400 mb-8">{t('reset_password.subtitle', { min: MIN_PASSWORD_LENGTH })}</p>

          {done ? (
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-green-400 text-sm font-medium">{t('reset_password.success_title')}</p>
                <p className="text-green-400/80 text-sm mt-1">
                  {t('reset_password.success_redirect')}
                </p>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="password" className="text-gray-300">{t('reset_password.new_password_label')}</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <PasswordInput
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-white/5 border-gray-800 text-white placeholder:text-gray-500"
                      placeholder={t('reset_password.new_password_placeholder')}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-gray-300">{t('reset_password.confirm_password_label')}</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <PasswordInput
                      id="confirm"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 bg-white/5 border-gray-800 text-white placeholder:text-gray-500"
                      placeholder={t('reset_password.confirm_password_placeholder')}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-amber-500 text-black hover:bg-amber-400 h-12"
                  disabled={loading}
                >
                  {loading ? t('reset_password.saving') : t('reset_password.submit_button')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </>
          )}

          <p className="mt-8 text-center text-gray-400">
            <Link to="/login" className="text-amber-400 hover:text-amber-300">
              {t('auth.back_to_sign_in')}
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-amber-900/30 to-orange-900/30 border-l border-gray-800">
        <div className="text-center px-12">
          <h2 className="text-4xl font-bold text-white mb-4">{t('auth.hero_title')}</h2>
          <p className="text-xl text-gray-400">{t('auth.hero_subtitle')}</p>
        </div>
      </div>
    </div>
  );
}
